// Súper online PR 4 (Tarea 7): la línea del carrito que arman la página de producto y la
// grilla del pasillo, y la vista de un producto retail.
//
// Por qué un módulo propio: la portada arma la línea en dos lugares (la tarjeta simple de
// `MenuSection` y el selector de peso/variante del modal). Si el pasillo y el producto
// armaran la suya, un cambio en una de las dos cuentas dejaría al carrito de un pasillo con
// otro total que el de la portada — invisible hasta el checkout. Acá se delega en las mismas
// funciones, y el precio fresco del carrito (D10) reconstruye con esta misma función.
//
// MÓDULO PURO: sin React ni imports de servidor (lo usan componentes de cliente).
import type { CartItem } from "@/components/microsite/types"
import { isItemAvailableNow, type ItemAvailability } from "@/lib/menu/availability"
import { buildMenuItemView, buildMenuItemViews, type MenuItemView, type MenuViewItem } from "@/lib/microsite/menuItemView"
import { buildRetailCartLine, needsRetailPicker, retailPickerSoldOut } from "@/lib/microsite/retailLine"
import { effectiveItemCardPrice, type PricingConfig } from "@/lib/utils/pricing"
import type { StorefrontRetailRow } from "./retailRow"

/** Horario de venta de la fila (las columnas llegan como `unknown` de PostgREST). */
function availabilityOf(item: StorefrontRetailRow): ItemAvailability {
  return {
    available_from: item.available_from,
    available_until: item.available_until,
    available_days: Array.isArray(item.available_days) ? (item.available_days as number[]) : null,
  }
}

/**
 * Línea del carrito para un producto de la tienda, o null si no se puede agregar:
 *   - agotado a mano (86), o con variantes sin ninguna activa / por peso sin unidad válida
 *     (la misma regla del «Agotado» de la tarjeta, `buildMenuItemView`);
 *   - fuera de su horario de venta, cuando el llamador pasa la zona del local (`tz`). Sin
 *     zona no se decide el horario: el precio fresco del carrito (D10) reconstruye sin ella
 *     y el horario lo vuelve a mirar el servidor (422) antes de cobrar;
 *   - peso inválido, sin variante elegida o con la variante agotada (`buildRetailCartLine`).
 *
 * Con peso o variante delega en `buildRetailCartLine` (precio CRUDO, R5). Si no, es la línea
 * simple de la tarjeta de la portada (`MenuSection.tsx`, «Agregar» directo): precio de
 * tarjeta efectivo, cantidad 1, sin modificadores.
 */
export function productCartLine(args: {
  item: StorefrontRetailRow
  cfg: PricingConfig
  cartId: string
  weightMilli?: number
  variantId?: string | null
  soldOutVariantIds?: ReadonlySet<string>
  /** Zona del local; ausente = no se decide el horario (ver arriba). */
  tz?: string
}): CartItem | null {
  const { item, cfg, cartId, weightMilli, variantId, soldOutVariantIds, tz } = args
  if (item.is_available === false || retailPickerSoldOut(item)) return null
  if (tz !== undefined && !isItemAvailableNow(availabilityOf(item), tz)) return null
  if (needsRetailPicker(item)) {
    return buildRetailCartLine({ item, cartId, weightMilli, variantId, soldOutVariantIds })
  }
  return {
    cartId,
    itemId: item.id,
    name: item.name,
    price: effectiveItemCardPrice(item.price, cfg),
    quantity: 1,
    modifiers: [],
  }
}

type ViewOpts = { pricingCfg: PricingConfig; tz: string; locale: "es" | "en"; photoWidth?: number }

/**
 * Vista de un producto de la tienda: la MISMA `buildMenuItemView` de la portada (precio,
 * nombre traducido, «Agotado», horario). La fila de la tienda trae `translations`,
 * `image_focal` y `available_days` como `unknown`; la vista los lee igual que con la fila de
 * la portada.
 */
export function storefrontItemView(item: StorefrontRetailRow, opts: ViewOpts): MenuItemView {
  return buildMenuItemView(item as unknown as MenuViewItem, opts)
}

export function storefrontItemViews(items: readonly StorefrontRetailRow[], opts: ViewOpts): MenuItemView[] {
  return buildMenuItemViews(items as unknown as MenuViewItem[], opts)
}
